import { useMemo } from 'react';
import { useUi } from './store';
import type { Lang } from './i18n';
import type { Currency } from './types';

/**
 * Presentation helpers.
 *
 * Every time the server sends is a UTC instant plus the branch's IANA zone, so
 * formatting always names that zone explicitly. Formatting in the visitor's own
 * zone would be actively wrong here: someone booking from abroad must see the
 * salon's clock, not their own.
 */

const localeFor = (lang: Lang): string => (lang === 'ar' ? 'ar-EG' : 'en-GB');

/**
 * Format an amount in the tenant's own currency.
 *
 * The decimal count comes from the tenant's configured precision, not from
 * Intl's idea of the currency and never from a hardcoded 2 — a Kuwaiti salon
 * quotes 3 decimals, and truncating those silently changes every price on the
 * page. An unrecognised code falls back to "amount CODE" rather than throwing,
 * since some tenants use non-ISO codes.
 */
export function formatMoney(amount: number, currency: Currency, lang: Lang): string {
  const precision = Number.isInteger(currency.precision) ? currency.precision : 2;
  try {
    return new Intl.NumberFormat(localeFor(lang), {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(amount);
  } catch {
    return `${amount.toFixed(precision)} ${currency.code}`;
  }
}

/**
 * `money(amount)` bound to the current tenant and language.
 *
 * Every price in the portal goes through this, so no component has to thread a
 * currency down or can accidentally render one tenant's prices in another's
 * currency.
 */
export function useMoney(): (amount: number) => string {
  const currency = useUi((s) => s.site?.currency);
  const lang = useUi((s) => s.lang);
  // Memoised for the same reason as useT: a new function identity every render
  // would invalidate any useCallback/useEffect that depends on it.
  return useMemo(() => {
    const resolved: Currency = currency ?? { code: 'EGP', name: null, name_ar: null, precision: 2 };
    return (amount: number) => formatMoney(amount, resolved, lang);
  }, [currency, lang]);
}

export function formatDuration(minutes: number, lang: Lang): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (lang === 'ar') {
    if (h && m) return `${h} س ${m} د`;
    if (h) return `${h} ساعة`;
    return `${m} دقيقة`;
  }
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m} min`;
}

export function formatTime(iso: string, timezone: string, lang: Lang): string {
  return new Intl.DateTimeFormat(localeFor(lang), {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso));
}

export function formatDateLong(iso: string, timezone: string, lang: Lang): string {
  return new Intl.DateTimeFormat(localeFor(lang), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: timezone,
  }).format(new Date(iso));
}

/** 'YYYY-MM-DD' → a short weekday + day-of-month pair for the date strip. */
export function formatDayChip(dateKey: string, lang: Lang): { weekday: string; day: string; month: string } {
  const date = new Date(`${dateKey}T12:00:00Z`);
  const fmt = (options: Intl.DateTimeFormatOptions): string =>
    new Intl.DateTimeFormat(localeFor(lang), { ...options, timeZone: 'UTC' }).format(date);
  return {
    weekday: fmt({ weekday: 'short' }),
    day: fmt({ day: 'numeric' }),
    month: fmt({ month: 'short' }),
  };
}

/** Today in a given IANA zone, as 'YYYY-MM-DD'. */
export function todayInZone(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '01';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function addDays(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days));
  return date.toISOString().slice(0, 10);
}

/** Countdown label for a hold, e.g. '4:07'. Returns null once elapsed. */
export function countdown(expiresAt: string): string | null {
  const remaining = new Date(expiresAt).getTime() - Date.now();
  if (remaining <= 0) return null;
  const total = Math.floor(remaining / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Which part of the day a slot falls in, for grouping the time picker. */
export function partOfDay(iso: string, timezone: string): 'morning' | 'afternoon' | 'evening' {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hour12: false, timeZone: timezone }).format(
      new Date(iso),
    ),
  );
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

/**
 * A `.ics` data URL so "add to calendar" works without a backend round trip or
 * a third-party service.
 */
export function icsDataUrl(args: {
  title: string;
  start: string;
  end: string;
  location: string;
  description: string;
  uid: string;
}): string {
  const stamp = (iso: string): string => `${iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '')}`;
  const escape = (value: string): string => value.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  const body = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Matix//Booking//EN',
    'BEGIN:VEVENT',
    `UID:${args.uid}`,
    `DTSTAMP:${stamp(new Date().toISOString())}`,
    `DTSTART:${stamp(args.start)}`,
    `DTEND:${stamp(args.end)}`,
    `SUMMARY:${escape(args.title)}`,
    `LOCATION:${escape(args.location)}`,
    `DESCRIPTION:${escape(args.description)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(body)}`;
}
